<script>
import { Group, Image, Rect, Circle, Text } from "svelte-konva";
import { SvelteMap } from "svelte/reactivity";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { markerState } from "@app/store/markerStore.svelte.js";
import { themeState } from "@app/store/themeStore.svelte.js";
import { getTimelinePalette } from "@app/lib/timeline/timelinePalette.js";
import {
    generateFilmstrip,
    canGenerateFilmstrip,
    computeFilmstripFrameTimes,
    FILMSTRIP_FRAME_WIDTH
} from "@app/lib/filmstrip.js";
import { applyFilmstripStartTime } from "@app/lib/timeline/editing.js";
import { frameGapToSeconds } from "@app/lib/timeline/snapping.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import tcLib from "@cclib/lib/timecode.js";

/**
 * Desktop-only film strip row. Renders a contiguous strip of frame thumbnails
 * centered on the selected event's start time (`frameCount` before, the start
 * frame, and `frameCount` after). A red dot marks the start frame; a colored
 * dot marks frames that line up with a marker in the selected marker list.
 * Clicking a thumbnail seeks the player to that frame's time. Double-clicking
 * sets the selected event's start time to that frame and trims the previous
 * event's end when it overlaps (respecting min frame gap).
 *
 * Frames are generated lazily off the main thread (ffmpeg worker) and cached on
 * disk. While the video is playing (or when no event is selected) the strip
 * clears its thumbnails and shows a hint instead.
 *
 * @typedef {Object} Props
 * @property {number} y
 * @property {number} width
 * @property {number} [height]
 */

/** @type {Props} */
let { y = 0, width = 0, height = 58 } = $props();

const GEN_WIDTH = FILMSTRIP_FRAME_WIDTH;
const GEN_DEBOUNCE_MS = 250;
const START_DOT_COLOR = "#E53935";
const CURRENT_DOT_COLOR = "#FDD835"; // yellow playhead marker
const DOT_RADIUS = 4;

let palette = $derived(getTimelinePalette(themeState.effective));

let selectedEventIndex = $derived.by(() => {
    const group = eventGroupState[projectState.selected];
    if (!group) return null;
    const idx = Array.isArray(group.selected) ? group.selected[0] : undefined;
    if (idx === undefined || idx === null) return null;
    return idx;
});

let selectedEvent = $derived.by(() => {
    const idx = selectedEventIndex;
    if (idx === null) return null;
    const ev = eventGroupState[projectState.selected]?.events?.[idx];
    if (!ev || !Number.isFinite(Number(ev.start))) return null;
    return ev;
});

let startTime = $derived(selectedEvent ? Number(selectedEvent.start) : null);
// Debounced copy of startTime that drives generation. Dragging an event's
// start handle commits ev.start on every dragmove, so reacting to startTime
// directly would spawn/terminate an ffmpeg worker many times per second and
// freeze the app. Waiting for the value to settle spawns a worker only once.
let debouncedStartTime = $state(null);
let frameCount = $derived(Math.min(30, Math.max(1, Math.round(Number(editorState.filmstripFrameCount) || 12))));
let mediaPath = $derived(projectState?.media?.localPath || "");

let markerList = $derived(markerState.lists?.[markerState.selected] || null);
let markerColor = $derived(markerList?.color || "#E74C3C");
let markerTimes = $derived.by(() => {
    if (!markerList) return [];
    return (markerList.markers || [])
        .map((mk) => Number(mk?.time))
        .filter((t) => Number.isFinite(t));
});

let frameRate = $derived(Number(projectState.frameRate) > 0 ? Number(projectState.frameRate) : 25);
let halfFrame = $derived(0.5 / frameRate);

// Image cache keyed by local output path. Blob URLs avoid brittle file://
// loading on Windows/Electron; bump loadVersion when decode completes.
const imageCache = new SvelteMap();
const blobUrls = new SvelteMap();
let loadVersion = $state(0);

async function ensureImage(outputPath) {
    if (!outputPath || !window.fs) return;
    const hit = imageCache.get(outputPath);
    if (hit !== undefined) return;

    imageCache.set(outputPath, null);
    try {
        const buffer = await window.fs.readFile(outputPath);
        const blob = new Blob([buffer], { type: "image/jpeg" });
        const blobUrl = URL.createObjectURL(blob);
        blobUrls.set(outputPath, blobUrl);

        const img = new window.Image();
        img.onload = () => {
            imageCache.set(outputPath, img);
            loadVersion += 1;
        };
        img.onerror = () => {
            imageCache.set(outputPath, false);
            loadVersion += 1;
        };
        img.src = blobUrl;
    } catch {
        imageCache.set(outputPath, false);
        loadVersion += 1;
    }
}

// Mirror startTime into debouncedStartTime once it settles. Deselecting clears
// immediately so the "Select an event" hint shows without delay.
$effect(() => {
    const start = startTime;
    if (start === null) {
        debouncedStartTime = null;
        return;
    }
    const timer = setTimeout(() => {
        debouncedStartTime = start;
    }, GEN_DEBOUNCE_MS);
    return () => clearTimeout(timer);
});

/** @type {{ index: number, offset: number, time: number, isStart: boolean, src: string }[]} */
let frames = $state([]);
let currentJob = null;

// Drive generation as a side effect of the relevant inputs. Cancels any
// in-flight job (terminating its worker) before starting a new one, and clears
// the strip while the video plays or when there is no usable selected event.
$effect(() => {
    const start = debouncedStartTime;
    const count = frameCount;
    const playing = playerState.playing;
    const path = mediaPath;
    const rate = frameRate;
    const duration = playerState.duration || projectState?.media?.duration;

    if (currentJob) {
        currentJob.cancel();
        currentJob = null;
    }

    if (playing || start === null || !path || !canGenerateFilmstrip(projectState)) {
        frames = [];
        return;
    }

    frames = computeFilmstripFrameTimes(start, count, rate, duration).map((f) => ({
        ...f,
        src: "",
        outputPath: ""
    }));

    const job = generateFilmstrip({
        projectState,
        startTime: start,
        frameCount: count,
        width: GEN_WIDTH,
        duration,
        onFrame: (frame) => {
            const target = frames[frame.index];
            if (target) {
                target.outputPath = frame.outputPath || "";
                target.src = frame.src;
                if (frame.outputPath) {
                    ensureImage(frame.outputPath);
                }
            }
        }
    });
    currentJob = job;

    return () => {
        job.cancel();
        for (const url of blobUrls.values()) {
            URL.revokeObjectURL(url);
        }
        blobUrls.clear();
    };
});

let totalFrames = $derived(frames.length);
let thumbWidth = $derived(totalFrames > 0 && width > 0 ? Math.max(6, Math.floor(width / totalFrames)) : 0);
let showTimecode = $derived(thumbWidth >= 56);
let timecodeBand = $derived(showTimecode ? 13 : 0);
let thumbHeight = $derived(Math.max(8, height - timecodeBand - 2));
let thumbDrawWidth = $derived(Math.max(4, thumbWidth - 1));

let renderFrames = $derived.by(() => {
    void loadVersion;
    if (thumbWidth <= 0) return [];
    return frames.map((f) => {
        const cacheKey = f.outputPath || f.src;
        const cached = cacheKey ? imageCache.get(cacheKey) : undefined;
        const image = cached instanceof window.Image ? cached : null;
        const loading = Boolean(cacheKey) && cached === null;
        const failed = cacheKey && cached === false;
        const hasMarker = markerTimes.some((mt) => Math.abs(mt - f.time) <= halfFrame);
        const isCurrent = Number.isFinite(playerState.time)
            && Math.abs(playerState.time - f.time) <= halfFrame;
        return {
            index: f.index,
            time: f.time,
            isStart: f.isStart,
            isCurrent,
            x: f.index * thumbWidth,
            src: f.src,
            image,
            loading,
            failed,
            hasMarker,
            tc: showTimecode
                ? tcLib.secToTc((f.time || 0) + (projectState.incode || 0), frameRate, projectState.dropFrame)
                : ""
        };
    });
});

let hint = $derived.by(() => {
    if (playerState.playing) return "";
    if (startTime === null) return "Select an event to view the film strip";
    return "";
});

function seekTo(time) {
    if (window.player && Number.isFinite(time)) {
        window.player.currentTime = Math.max(0, time);
    }
}

function handleFrameClick(time, e) {
    if (e) e.cancelBubble = true;
    seekTo(time);
}

function handleFrameDoubleClick(time, e) {
    if (e) e.cancelBubble = true;
    const groupIdx = projectState.selected;
    const eventIndex = selectedEventIndex;
    const group = eventGroupState[groupIdx];
    if (eventIndex === null || !group || !Number.isFinite(time)) return;

    const frameGapSec = frameGapToSeconds(
        editorState.minFrameGap || 0,
        projectState.frameRate || 29.97
    );
    const changed = applyFilmstripStartTime({
        events: group.events,
        eventIndex,
        newStart: time,
        frameGapSec
    });
    if (!changed) return;

    try {
        insertHistoryAction({
            name: "update timecode",
            eventGroup: groupIdx,
            snapshots: [
                { store: "eventGroupState", value: JSON.stringify(eventGroupState) }
            ]
        });
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[filmstrip] history commit failed:", err?.message);
    }

    seekTo(time);
}
</script>

<Group y={y}>
    <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill={palette.filmstripBg}
        listening={false} />

    {#if renderFrames.length > 0}
        <!--
            Thumbnails live in their own group. Konva z-order between sibling
            groups is fixed by group order, so images added asynchronously to
            this group always stay below the overlay group's dots/timecodes.
        -->
        <Group>
            {#each renderFrames as frame (`thumb-${frame.index}`)}
                <Rect
                    x={frame.x}
                    y={0}
                    width={thumbDrawWidth}
                    height={thumbHeight}
                    fill={palette.filmstripThumbBg}
                    stroke={frame.isStart ? START_DOT_COLOR : palette.filmstripFrameBorder}
                    strokeWidth={frame.isStart ? 2 : 1}
                    onpointerdown={(e) => handleFrameClick(frame.time, e)}
                    onpointerdblclick={(e) => handleFrameDoubleClick(frame.time, e)} />
                {#if frame.image}
                    <Image
                        x={frame.x}
                        y={0}
                        width={thumbDrawWidth}
                        height={thumbHeight}
                        image={frame.image}
                        onpointerdown={(e) => handleFrameClick(frame.time, e)}
                        onpointerdblclick={(e) => handleFrameDoubleClick(frame.time, e)} />
                {:else if frame.loading || frame.src === ""}
                    <Text
                        x={frame.x}
                        y={Math.floor(thumbHeight / 2 - 5)}
                        width={thumbDrawWidth}
                        text="..."
                        align="center"
                        fontSize={10}
                        fill={palette.filmstripPlaceholderText}
                        listening={false} />
                {:else if frame.failed}
                    <Text
                        x={frame.x}
                        y={Math.floor(thumbHeight / 2 - 5)}
                        width={thumbDrawWidth}
                        text="!"
                        align="center"
                        fontSize={10}
                        fill={palette.filmstripPlaceholderText}
                        listening={false} />
                {/if}
            {/each}
        </Group>

        <!-- Overlay group: dots and timecodes, always above thumbnails. -->
        <Group>
            {#each renderFrames as frame (`overlay-${frame.index}`)}
                {#if frame.hasMarker}
                    <Circle
                        x={frame.x + thumbDrawWidth - DOT_RADIUS - 2}
                        y={DOT_RADIUS + 2}
                        radius={DOT_RADIUS}
                        fill={markerColor}
                        stroke="#000000"
                        strokeWidth={0.5}
                        listening={false} />
                {/if}
                {#if frame.isStart}
                    <Circle
                        x={frame.x + thumbDrawWidth / 2}
                        y={DOT_RADIUS + 2}
                        radius={DOT_RADIUS}
                        fill={START_DOT_COLOR}
                        stroke="#000000"
                        strokeWidth={0.5}
                        listening={false} />
                {/if}
                {#if frame.isCurrent}
                    <Circle
                        x={frame.x + thumbDrawWidth / 2}
                        y={thumbHeight - DOT_RADIUS - 2}
                        radius={DOT_RADIUS}
                        fill={CURRENT_DOT_COLOR}
                        stroke="#000000"
                        strokeWidth={0.5}
                        listening={false} />
                {/if}

                {#if showTimecode && frame.tc}
                    <Text
                        x={frame.x}
                        y={thumbHeight + 1}
                        width={thumbDrawWidth}
                        text={frame.tc}
                        align="center"
                        fontSize={9}
                        fill={palette.filmstripTimecodeText}
                        listening={false} />
                {/if}
            {/each}
        </Group>
    {:else if hint}
        <Text
            x={0}
            y={Math.floor(height / 2 - 6)}
            width={width}
            text={hint}
            align="center"
            fontSize={11}
            fill={palette.filmstripPlaceholderText}
            listening={false} />
    {/if}
</Group>
